import { RefObject, useCallback, useEffect, useState } from 'react';

type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type WebkitHTMLElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

/** True when the document is in standard or webkit fullscreen. */
export const isDocumentFullscreen = (): boolean => {
  const doc = document as WebkitDocument;
  return !!(doc.fullscreenElement || doc.webkitFullscreenElement);
};

/** Exit browser fullscreen (standard + Safari/webkit). */
export const exitDocumentFullscreen = (): void => {
  if (!isDocumentFullscreen()) return;
  const doc = document as WebkitDocument;
  if (doc.exitFullscreen) {
    void doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    void doc.webkitExitFullscreen();
  }
};

export const useFullScreen = (ref: RefObject<HTMLDivElement & any>) => {
  const [fullScreen, setFullScreen] = useState(false);
  const hasFullscreen = !!(
    ref.current?.requestFullscreen ||
    (ref.current as WebkitHTMLElement | null)?.webkitRequestFullscreen
  );

  useEffect(() => {
    const handler = () => {
      setFullScreen(isDocumentFullscreen());
    };
    const reference = ref.current;
    if (reference) {
      reference.addEventListener('fullscreenchange', handler);
      reference.addEventListener('webkitfullscreenchange', handler);

      return () => {
        reference.removeEventListener('fullscreenchange', handler);
        reference.removeEventListener('webkitfullscreenchange', handler);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExitFullScreen = () => {
    exitDocumentFullscreen();
    setFullScreen(false);
  };

  const handleOpenFullScreen = () => {
    const el = ref.current as (HTMLDivElement & WebkitHTMLElement) | null;
    if (!el) return;
    if (el.requestFullscreen) {
      void el.requestFullscreen();
      setFullScreen(true);
    } else if (el.webkitRequestFullscreen) {
      void el.webkitRequestFullscreen();
      setFullScreen(true);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const close = useCallback(handleExitFullScreen, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const open = useCallback(handleOpenFullScreen, []);

  return {
    close,
    open,
    hasFullscreen,
    fullScreen
  };
}
